import React from 'react';
import { Link } from 'react-router-dom';

export function Logo({ inverse = false, size = 22, markOnly = false, product, className = '' }) {
  return (
    <span className={"inline-flex items-baseline gap-2.5 " + className}>
      <span className="inline-flex items-center" style={{ gap: size * 0.4 }}>
        <img src={inverse ? '/brand/sudvik-mark-inverse.svg' : '/brand/sudvik-mark.svg'} alt="Südvik" style={{ height: size * 1.15, display: 'block' }} />
        {!markOnly && <span className="font-medium tracking-[-0.02em] leading-none" style={{ fontSize: size * 1.25, color: inverse ? '#fff' : 'var(--ls-ink-900)' }}>Südvik</span>}
      </span>
      {product && <span className={"text-[13px] " + (inverse ? 'text-white/70' : 'text-gray-600')}>{product}</span>}
    </span>
  );
}

export function Button({ variant = 'primary', size = 'md', to, href, className = '', children, iconLeft, iconRight, ...rest }) {
  const cls = ['sv-btn', 'sv-btn-' + variant, size === 'sm' ? 'sv-btn-sm' : size === 'lg' ? 'sv-btn-lg' : '', className].join(' ');
  const content = <>{iconLeft}{children}{iconRight}</>;
  if (to) return <Link to={to} className={cls} {...rest}>{content}</Link>;
  if (href) return <a href={href} className={cls} {...rest}>{content}</a>;
  return <button type="button" className={cls} {...rest}>{content}</button>;
}

export function Card({ title, action, className = '', padding = 'p-6', children, ...rest }) {
  return (
    <div className={"sv-card " + className} {...rest}>
      <div className={padding}>
        {(title || action) && (
          <div className="flex items-center justify-between gap-3 mb-4">
            {title && <h3 className="text-xl font-medium tracking-[-0.01em]">{title}</h3>}
            {action}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

export function Kpi({ label, value, delta, tone, className = '', ...rest }) {
  const toneCls = tone === 'bad' ? 'text-error-700' : tone === 'good' ? 'text-success-700' : 'text-gray-600';
  return (
    <div className={"sv-card p-5 " + className} {...rest}>
      <div className="text-sm text-gray-600">{label}</div>
      <div className="flex items-baseline gap-3 mt-2">
        <div className="text-4xl sv-display">{value}</div>
        {delta && <span className={"text-[13px] " + toneCls}>{delta}</span>}
      </div>
    </div>
  );
}

const STATUS_TONE = { pending: 'warning', pending_approval: 'warning', confirmed: 'info', in_transit: 'info', delivered: 'success', approved: 'success', active: 'success', paid: 'success', completed: 'success', cancelled: 'danger', rejected: 'danger', failed: 'danger', new: 'info', in_progress: 'warning', resolved: 'success' };

export function Badge({ status, tone, children, className = '', ...rest }) {
  const t = tone || STATUS_TONE[status] || 'neutral';
  return <span className={"sv-badge sv-badge-" + t + " " + className} {...rest}>{children || String(status || '').replace(/_/g, ' ')}</span>;
}

export function Tag({ mono = false, children, className = '', ...rest }) {
  return <span className={"sv-tag " + (mono ? 'sv-mono ' : '') + className} {...rest}>{children}</span>;
}

export function Tabs({ tabs, active, onChange, className = '' }) {
  return (
    <div className={"sv-tabs " + className} role="tablist">
      {tabs.map(function (t) {
        return (
          <button key={t.id} type="button" role="tab" className="sv-tab" data-active={t.id === active} onClick={function () { onChange(t.id); }} data-testid={"tab-" + t.id}>
            {t.label}{t.count !== undefined && <span className="sv-tab-count">{t.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Field({ label, hint, error, children, className = '' }) {
  return (
    <label className={"block " + className}>
      {label && <span className="sv-label">{label}</span>}
      {children}
      {error ? <span className="sv-hint text-error-700">{error}</span> : hint ? <span className="sv-hint">{hint}</span> : null}
    </label>
  );
}

export function Kicker({ children, className = '' }) {
  return <p className={"sv-kicker " + className}>{children}</p>;
}

export function EmptyState({ title, text, action }) {
  return (
    <div className="text-center py-16 px-6" data-testid="empty-state">
      <p className="text-lg text-ink">{title}</p>
      {text && <p className="text-sm text-gray-600 mt-2 max-w-sm mx-auto">{text}</p>}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}
